import { FunctionComponent } from "react";
import { SkyQTvGuide, SkyQTvGuideProgram } from "../../../../api/tvChannelApi";
import { Guid } from "../../../../api/baseApi";
import { calculateEventTime, extractTimeFromDateTime } from "./utils";

interface TvGuideGridProps {
    channels: SkyQTvGuide[];
    selectedProgramIds: Guid[];
    onSelectProgram: (channel: SkyQTvGuide, program: SkyQTvGuideProgram) => void;
}

const renderTimeline = (withPseudoLines: boolean = false) => {
    const items = [];
    for (let i = 0; i < 24; i++) {
        const hh = i.toString().padStart(2, '0');
        if (withPseudoLines) {
            items.push(
                <div key={`hourP-${i}`} className="sky-guide-timeline-pseudo-slot"></div>,
                <div key={`half-hourP-${i}`} className="sky-guide-timeline-pseudo-slot"></div>
            );
        } else {
            items.push(
                <div key={`hour-${i}`} className="sky-guide-timeline-slot">{hh}:00</div>,
                <div key={`half-hour-${i}`} className="sky-guide-timeline-slot">{hh}:30</div>
            );
        }
    }
    return items;
};

const TvGuideGrid: FunctionComponent<TvGuideGridProps> = ({ channels, selectedProgramIds, onSelectProgram }) => {
    return (
        <div className="sky-guide-wrapper">
            <div className="sky-guide-block">
                <div className="sky-guide-timeline">
                    <div className=""></div>
                    {renderTimeline()}
                </div>
                <div className="sky-guide-timeline-pseudo">
                    <div className="sky-guide-timeline-pseudo-slot"></div>
                    {renderTimeline(true)}
                </div>
                {channels.map(channel => (
                    <div key={channel.tvChannelId} className="sky-guide-row">
                        <div className="sky-guide-channel-name">{channel.tvChannelName}</div>
                        {channel.programs.map(program => {
                            const startTime = extractTimeFromDateTime(program.startTime);
                            const endTime = extractTimeFromDateTime(program.endTime);
                            const { startInterval, offsetPercentage, eventDurationPercentage } = calculateEventTime(startTime, endTime, 30);
                            const programId = `${channel.tvChannelId}-${program.startTime}-${program.endTime}`;
                            const isSelected = selectedProgramIds.includes(programId);
                            return (
                                <div key={`${program.startTime}-${program.endTime}`}
                                    className={`sky-guide-cell${isSelected ? ' sky-guide-cell-selected' : ''}`}
                                    style={{
                                        ['--column-start']: `${startInterval + 1}`,
                                        ['--left']: `${offsetPercentage}%`,
                                        ['--width']: `${eventDurationPercentage}%`,
                                    } as React.CSSProperties}
                                    title={program.fullDescription || undefined}
                                    onClick={() => onSelectProgram(channel, program)}>
                                    <div className="sky-guide-program-info">
                                        <p className="sky-guide-program-title">
                                            {program.detailsUrl ? (
                                                <a
                                                    href={program.detailsUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}>
                                                    {program.name}
                                                </a>
                                            ) : program.name}
                                        </p>
                                        <p className="sky-guide-program-short-desc">{program.shortDescription || '\u00A0'}</p>
                                        <p className="sky-guide-program-time">{startTime.slice(0, -3)} - {endTime.slice(0, -3)}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TvGuideGrid;
